import type { StravaStatus } from '@painpal/contracts';
import { useDisconnectStrava, useStravaConnect } from '../../api/mutations';
import { useAccount, useStravaStatus } from '../../api/queries';
import { ConfirmButton } from '../../components/ConfirmButton';
import { ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { PoweredByStrava } from '../../components/PoweredByStrava';
import { StravaConnectButton } from '../../components/StravaConnectButton';
import { StravaReturnError } from '../../components/StravaReturnError';
import { ApiRequestError, errorMessage } from '../../lib/api';
import { formatInt, formatRelative } from '../../lib/format';
import { plural } from '../../lib/plural';
import { importSummary, needsReconnect, UNREADABLE_NOTE } from '../../lib/strava';
import { KeyValue } from './KeyValue';
import { SET_PASSWORD_ID } from './SetPasswordCard';
import styles from './settings.module.css';

export const ONLY_SIGN_IN_MESSAGE =
  'Strava is how you sign in to PainPal, so it can’t be disconnected. To leave, delete your account below.';
export const SET_PASSWORD_FIRST = 'Set a password first, so you can still sign in';

export function stravaActionMessage(error: Error): string {
  return error instanceof ApiRequestError && error.code === 'only-sign-in-method' ? ONLY_SIGN_IN_MESSAGE : errorMessage(error);
}

export function athleteLabel(strava: StravaStatus): string {
  if (strava.athleteName !== null) return strava.athleteName;
  return strava.athleteId === null ? '—' : `#${strava.athleteId}`;
}

const KEPT = 'Uploaded files, thresholds and your plan stay.';

export function disconnectConsequence(activities: number): string {
  if (activities === 0) return `Disconnecting stops new imports from Strava. ${KEPT}`;
  return `Disconnecting deletes the ${formatInt(activities)} ${plural(activities, 'activity', 'activities')} imported from Strava. ${KEPT}`;
}

export function disconnectPrompt(activities: number): string {
  if (activities === 0) return 'DISCONNECT STRAVA?';
  return `DISCONNECT AND DELETE ${formatInt(activities)} STRAVA ${plural(activities, 'ACTIVITY', 'ACTIVITIES')}?`;
}

export function revokedNote(activities: number): string {
  const what = activities > 0 ? 'the activities imported from Strava are being deleted' : 'the activities imported from Strava were deleted';
  return `Strava access was revoked, so ${what}. ${KEPT} Reconnect to import your history again.`;
}

export function stravaChip(strava: StravaStatus): { label: string; tone: 'green' | 'hot' | 'neutral' } {
  if (needsReconnect(strava)) return { label: 'RECONNECT NEEDED', tone: 'hot' };
  if (!strava.connected) return { label: 'NOT CONNECTED', tone: 'neutral' };
  return { label: 'CONNECTED', tone: 'green' };
}

interface BodyProps {
  strava: StravaStatus; onConnect: () => void; connecting: boolean; onDisconnect: () => void; disconnecting: boolean; locked: boolean;
}

function reconnectNote(strava: StravaStatus): string {
  if (strava.status === 'revoked') return revokedNote(strava.activities);
  if (strava.status === 'unreadable') return UNREADABLE_NOTE;
  return 'Connect once and new rides import automatically, plus your history, newest first.';
}

function StravaBody({ strava, onConnect, connecting, onDisconnect, disconnecting, locked }: BodyProps) {
  if (!strava.connected || needsReconnect(strava)) {
    const note = reconnectNote(strava);
    return (
      <>
        <p className={styles.note}>{note}</p>
        <StravaConnectButton onClick={onConnect} busy={connecting} />
      </>
    );
  }
  return (
    <>
      <dl className={styles.kvList}>
        <KeyValue k="ATHLETE" v={athleteLabel(strava)} />
        <KeyValue k="LAST NEW RIDE" v={strava.lastActivityAt === null ? '—' : formatRelative(strava.lastActivityAt, new Date())} />
        <KeyValue k="HISTORY IMPORT" v={importSummary(strava.backfill)} />
      </dl>
      <ConfirmButton
        label="DISCONNECT STRAVA" confirmLabel="YES, DISCONNECT" prompt={disconnectPrompt(strava.activities)} className="btn btn-outline"
        busy={disconnecting || locked} onConfirm={onDisconnect}
      />
      {locked
        ? <p className={styles.note}><a className={styles.noteLink} href={`#${SET_PASSWORD_ID}`}>{SET_PASSWORD_FIRST}</a></p>
        : <p className={styles.note}>{disconnectConsequence(strava.activities)}</p>}
      <PoweredByStrava />
    </>
  );
}

export function StravaCard({ returnError }: { returnError?: string }) {
  const strava = useStravaStatus();
  const connect = useStravaConnect();
  const disconnect = useDisconnectStrava();
  const account = useAccount();
  const actionError = connect.error ?? disconnect.error;
  return (
    <section className={styles.card} data-tone="strong" aria-labelledby="strava-title">
      <header className={styles.cardHead}>
        <h2 id="strava-title" className={styles.cardTitle}>STRAVA</h2>
        {strava.isSuccess && <span className={styles.chip} data-tone={stravaChip(strava.data).tone}>{stravaChip(strava.data).label}</span>}
      </header>
      <StravaReturnError error={returnError} path="/settings" />
      {strava.isPending && <LoadingBlock label="CHECKING STRAVA" height={80} />}
      {strava.isError && <ErrorPanel error={strava.error} onRetry={() => void strava.refetch()} />}
      {strava.isSuccess && (
        <StravaBody
          strava={strava.data} onConnect={() => connect.mutate({ returnTo: '/settings' })} connecting={connect.isPending}
          onDisconnect={() => disconnect.mutate()} disconnecting={disconnect.isPending}
          locked={account.data?.hasPassword === false}
        />
      )}
      {actionError !== null && <p role="alert" className={`alert-hot ${styles.formError}`}>{stravaActionMessage(actionError)}</p>}
    </section>
  );
}
